/**
 * RÉFÉRENTIELS → CHAMPS PERSONNALISÉS — la moitié CONFIGURATION (la fiche article remplit, elle).
 *
 * Transposé du relevé maquette (`fgT5`) : en-tête + « + Nouveau champ », composeur en panneau teal
 * (nom + type segmenté + valeurs possibles pour une liste), état vide honnête, lignes
 * libellé · pastille de type · aperçu des options · corbeille, et la SUPPRESSION ARMÉE EN LIGNE
 * (bandeau ambre « Supprimer pour toutes les fiches ? Les valeurs déjà saisies seront perdues ») —
 * jamais un confirm natif. Le serveur tient la promesse : supprimer PURGE les valeurs, et le compte
 * réel de fiches purgées se dit au toast.
 */
import { useState } from 'react';
import { showToast, Trans } from '@orgazone/ui-web';
import { api, type CustomField, type CustomFieldType } from '../api';
import { key, t, type Translatable } from '../i18n';

const TYPES: { v: CustomFieldType; l: Translatable }[] = [
  { v: 'txt', l: key('Texte') }, { v: 'num', l: key('Nombre') }, { v: 'date', l: key('Date') },
  { v: 'bool', l: key('Oui / Non') }, { v: 'list', l: key('Liste') },
];
/** Le libellé d’un type de champ, résolu au rendu — un type inconnu se montre sous son code. */
export const libelleDeType = (type: CustomFieldType): string => {
  const l = TYPES.find((x) => x.v === type)?.l;
  return l ? t(l) : type;
};

export function OngletChamps({ jeu, failure, reload }: {
  jeu: CustomField[] | null; failure: string | null; reload: () => void;
}) {
  const [composeur, setComposeur] = useState(false);
  const [nom, setNom] = useState('');
  const [type, setType] = useState<CustomFieldType>('txt');
  const [options, setOptions] = useState('');
  const [armee, setArmee] = useState<string | null>(null);
  const [liveCount, setEnCours] = useState(false);

  const ready = !!nom.trim() && (type !== 'list' || options.split(',').some((o) => o.trim()));

  const create = async () => {
    setEnCours(true);
    try {
      await api.createCustomField({
        label: nom.trim(), fieldType: type,
        options: type === 'list' ? options.split(',').map((o) => o.trim()).filter(Boolean) : [],
      });
      showToast(t('Champ « {v} » créé — il apparaît sur toutes les fiches.', { v: nom.trim() }));
      setComposeur(false); setNom(''); setType('txt'); setOptions('');
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const remove = async (c: CustomField) => {
    setEnCours(true);
    try {
      const r = await api.deleteCustomField(c.id);
      // Le compte RÉEL : « 12 fiches nettoyées » dit ce qui vient d'être perdu, comme promis.
      showToast(r.purged > 0
        ? t('« {label} » supprimé — {n} fiche nettoyée.', { label: c.label, n: r.purged })
        : t('« {label} » supprimé — aucune fiche ne portait de valeur.', { label: c.label }));
      setArmee(null);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {t('Le référentiel n’a pas pu être lu : {failure}. Ce n’est pas « aucun champ ».', { failure })}</p>
    );
  }
  if (!jeu) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du référentiel…')}</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Champs personnalisés')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            
            {t('Définis une fois pour toute l’enseigne — chaque champ apparaît dans l’onglet « Champs perso » de toutes les fiches articles.')}
          </p>
        </div>
        <button type="button" onClick={() => setComposeur(true)}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          
          {t('+ Nouveau champ')}
        </button>
      </div>

      {composeur ? (
        <div style={{ border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ flex: 1, minWidth: 200 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Nom du champ')}</span>
              <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder={t('Réf. interne, emplacement, norme…')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <span>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Type')}</span>
              <span style={{ display: 'inline-flex', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden', background: 'var(--paper)' }}>
                {TYPES.map((entree) => (
                  <button aria-pressed={type === entree.v} key={entree.v} type="button" onClick={() => setType(entree.v)} style={{
                    height: 38, padding: '0 11px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                    background: type === entree.v ? 'var(--teal-800)' : 'transparent',
                    color: type === entree.v ? '#fff' : 'var(--ink-700)', fontSize: '12.5px', fontWeight: 700,
                  }}>{t(entree.l)}</button>
                ))}
              </span>
            </span>
          </div>
          {type === 'list' ? (
            <label style={{ display: 'block', marginTop: 11 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Valeurs possibles — séparées par des virgules')}</span>
              <input value={options} onChange={(e) => setOptions(e.target.value)} placeholder={t('Neuf, Reconditionné, Occasion')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" disabled={!ready || liveCount} onClick={() => void create()}
              style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: ready ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: ready ? 'pointer' : 'default' }}>
              
              {t('Créer le champ')}
            </button>
            <button type="button" onClick={() => { setComposeur(false); setNom(''); setOptions(''); }}
              style={{ height: 38, padding: '0 13px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
              
              {t('Annuler')}
            </button>
          </div>
        </div>
      ) : null}

      {jeu.length === 0 ? (
        <div style={{ padding: '34px 20px', textAlign: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun champ personnalisé')}</div>
          <p style={{ margin: '4px auto 0', fontSize: '12.5px', color: 'var(--ink-500)', maxWidth: 400, lineHeight: 1.55 }}>
            
            {t('Créez vos propres informations de fiche — une référence interne, un emplacement, une écotaxe — sans attendre une évolution du produit.')}
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {jeu.map((c) => (
            <div key={c.id}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px,1fr) 96px minmax(0,1.25fr) 34px', gap: 12, padding: '12px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
                <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{libelleDeType(c.fieldType)}</span>
                <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.fieldType === 'list' ? c.options.join(', ') : ''}
                </span>
                <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {armee !== c.id ? (
                    <button type="button" aria-label={t('Supprimer le champ {label}', { label: c.label })} title={t('Supprimer le champ')} onClick={() => setArmee(c.id)}
                      style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" /></svg>
                    </button>
                  ) : null}
                </span>
              </div>
              {armee === c.id ? (
                // ⚠️ LA CONFIRMATION EST EN LIGNE, sous la ligne visée (relevé) — jamais un confirm natif.
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 16px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--ink-100)' }}>
                  <span style={{ flex: 1, minWidth: 200, fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5 }}>
                    <Trans k='Supprimer « {label} » pour <1>toutes les fiches</1> ? Les valeurs déjà saisies seront perdues.' vars={{ label: c.label }} parts={[(x) => <strong>{x}</strong>]} /></span>
                  <button type="button" disabled={liveCount} onClick={() => void remove(c)}
                    style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-fg)', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>
                    
                    {t('Supprimer')}
                  </button>
                  <button type="button" onClick={() => setArmee(null)}
                    style={{ height: 32, padding: '0 13px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    
                    {t('Garder')}
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        
        {t('Côté base : une table dédiée — une ligne par champ défini (configuration en JSON) et un JSON de valeurs par article. La table des articles n’est jamais modifiée.')}
      </div>
    </div>
  );
}
